// The live drag of the context sheet of a telephone (ui-map-interface.md
// §12.0 "The context sheet", R2008). The season overlay and the arcade
// host both run their sheet through it.
//
// A drag starts on the grab handle (`.cs-grab`), the peek line
// (`.cs-line`) or the card head (`.ck-head`), never on a button of them.
// The sheet follows the finger: each frame writes ONE custom property,
// `--cs-h`, on the host through rAF, and `.cs-drag` makes the card take
// that height. The first pointerdown reads the height of the card once;
// no pointermove reads a layout. When the drag passes `ctx_swipe_px` the
// host renders the other state at that moment, once per crossing, and
// `.cs-armed` gives the grab handle the accent ink, so the player sees
// that the release will hold. A drag back under the threshold reverts.
//
// The release snaps the card to the height of the state it holds, with
// the sheet ease `--dock-ease` over `--dock-ms`; reduced motion snaps at
// once. A tap (a move under `ctx_tap_px`) on the grab handle moves the
// sheet to the other state, and a tap on the line of a closed sheet
// raises it. A keyboard press on the grab handle (a click with no pointer
// behind it) moves the sheet to the other state too.

import { reducedMotion } from '../render/motion';
import { ctxCapPx, ctxDragHeight, ctxDragState, ctxTap, ctxTapGo } from './ctxsheet';
import { chromeViewport, toChromePx } from './uiscale';

/** What the drag needs of the host of a sheet. */
export interface SheetDragHost {
  /** The host element: it takes `.cs-drag`, `.cs-armed` and `--cs-h`. */
  readonly el: HTMLElement;
  /** True while the sheet takes a drag: a telephone, a card on the glass,
   *  no plot grip held. */
  live(): boolean;
  /** The state the sheet holds now. */
  raised(): boolean;
  /** Puts the sheet in a state: the host renders its card in it. */
  set(raised: boolean): void;
}

/** One drag in flight. */
interface Drag {
  id: number;
  y0: number;
  /** The start height of the card, in chrome px. */
  h0: number;
  lo: number;
  hi: number;
  start: boolean;
  /** The state the drag holds now. */
  now: boolean;
  onGrab: boolean;
  moved: boolean;
  dy: number;
}

/** The card of the host, which takes the height. */
function cardOf(el: HTMLElement): HTMLElement | null {
  return el.querySelector<HTMLElement>(':scope > .ck');
}

/**
 * The drag of one sheet. `destroy` takes every listener off.
 */
export class SheetDrag {
  private drag: Drag | null = null;
  private frame = 0;
  /** The height of the closed card, read at the start of a drag from the
   *  closed sheet, so a drag down from the raised sheet knows its floor. */
  private closedPx = 0;
  private snapEnd = 0;
  private readonly offs: (() => void)[] = [];

  constructor(private readonly host: SheetDragHost) {
    const el = host.el;
    const down = (ev: PointerEvent) => this.down(ev);
    const click = (ev: MouseEvent) => {
      // A pointer tap is the drag's own (`up`); a click with no pointer
      // behind it is a keyboard press on the grab handle.
      const t = ev.target as HTMLElement;
      if (ev.detail !== 0 || !t.closest('.cs-grab') || !host.live()) return;
      host.set(!host.raised());
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('click', click);
    this.offs.push(() => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('click', click);
    });
  }

  /** True while a finger holds the sheet. */
  get active(): boolean { return this.drag !== null; }

  private down(ev: PointerEvent) {
    if (this.drag || ev.button > 0 || !this.host.live()) return;
    const t = ev.target as HTMLElement;
    const onGrab = t.closest('.cs-grab') !== null;
    if (!onGrab && (t.closest('button, a, input, select, textarea') || !t.closest('.cs-line, .ck-head'))) return;
    const card = cardOf(this.host.el);
    if (!card) return;
    this.endSnap();
    const start = this.host.raised();
    // The one layout read of the drag.
    const h0 = card.offsetHeight;
    if (!start) this.closedPx = h0;
    this.drag = {
      id: ev.pointerId, y0: ev.clientY, h0,
      lo: start ? this.closedPx : h0,
      hi: Math.max(h0, ctxCapPx(chromeViewport().h, 'object')),
      start, now: start, onGrab, moved: false, dy: 0,
    };
    window.addEventListener('pointermove', this.move);
    window.addEventListener('pointerup', this.up);
    window.addEventListener('pointercancel', this.cancel);
  }

  private readonly move = (ev: PointerEvent) => {
    const d = this.drag;
    if (!d || d.id !== ev.pointerId) return;
    d.dy = toChromePx(ev.clientY - d.y0);
    if (!d.moved) {
      if (ctxTap(d.dy)) return;
      d.moved = true;
      this.host.el.classList.add('cs-drag');
    }
    ev.preventDefault();
    const now = ctxDragState(d.start, d.dy);
    if (now !== d.now) {
      // The state commits during the move, once per crossing.
      d.now = now;
      this.host.el.classList.toggle('cs-armed', now !== d.start);
      this.host.set(now);
    }
    if (this.frame === 0) this.frame = requestAnimationFrame(this.paint);
  };

  /** One frame of the drag: the one custom property the card reads. */
  private readonly paint = () => {
    this.frame = 0;
    const g = this.drag;
    if (g) this.host.el.style.setProperty('--cs-h', `${Math.round(ctxDragHeight(g.h0, g.dy, g.lo, g.hi))}px`);
  };

  private readonly up = (ev: PointerEvent) => {
    const d = this.drag;
    if (!d || d.id !== ev.pointerId) return;
    this.stop();
    // The release reads its own place too: a fast flick can end before a
    // pointermove reached the sheet.
    const dy = toChromePx(ev.clientY - d.y0);
    if (!d.moved && ctxTap(dy)) {
      const go = ctxTapGo(d.start, d.onGrab);
      if (go !== null && go !== d.start) this.host.set(go);
      return;
    }
    const now = ctxDragState(d.start, dy);
    if (now !== d.now) this.host.set(now);
    this.snap(Math.round(ctxDragHeight(d.h0, dy, d.lo, d.hi)));
  };

  private readonly cancel = (ev: PointerEvent) => {
    const d = this.drag;
    if (!d || d.id !== ev.pointerId) return;
    this.stop();
    // A cancelled drag keeps the state it committed, and snaps to it.
    if (d.moved) this.snap(Math.round(ctxDragHeight(d.h0, d.dy, d.lo, d.hi)));
  };

  /** Ends the drag: the listeners and the frame go. */
  private stop() {
    this.drag = null;
    window.removeEventListener('pointermove', this.move);
    window.removeEventListener('pointerup', this.up);
    window.removeEventListener('pointercancel', this.cancel);
    if (this.frame !== 0) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.host.el.classList.remove('cs-armed');
  }

  /**
   * The snap of the release: the card goes from the height of the drag to
   * the height of its state. The card's height at rest is read once, with
   * the drag classes off, and the card eases to it through `.cs-snap`.
   */
  private snap(from: number) {
    const el = this.host.el;
    el.classList.remove('cs-drag');
    el.style.removeProperty('--cs-h');
    const card = cardOf(el);
    if (!card || reducedMotion()) return;
    const to = card.offsetHeight;
    if (to === from) return;
    el.style.setProperty('--cs-h', `${from}px`);
    el.classList.add('cs-drag', 'cs-snap');
    void card.offsetHeight;
    el.style.setProperty('--cs-h', `${to}px`);
    const ms = parseFloat(getComputedStyle(el).getPropertyValue('--dock-ms')) || 0;
    this.snapEnd = window.setTimeout(() => this.endSnap(), ms + 50);
  }

  /** Gives the card its height back to the stylesheet. */
  private endSnap() {
    if (this.snapEnd !== 0) clearTimeout(this.snapEnd);
    this.snapEnd = 0;
    this.host.el.classList.remove('cs-drag', 'cs-snap');
    this.host.el.style.removeProperty('--cs-h');
  }

  destroy() {
    this.stop();
    this.endSnap();
    for (const off of this.offs) off();
  }
}
